/**
 * Slugs the public site already routes, or that would collide with the app's own paths. A gym page
 * may not take one, or it would shadow a built-in route and the gym could not get it back.
 */
export const RESERVED_PAGE_SLUGS = [
  'p',
  'plans',
  'classes',
  'trainers',
  'contact',
  'app',
  'admin',
  'platform',
  'login',
  'register',
  'api',
  'storage',
  'health',
] as const;

export const PAGE_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export function isReservedSlug(slug: string): boolean {
  return (RESERVED_PAGE_SLUGS as readonly string[]).includes(slug);
}

/** The national romanisation of Georgian (2002), letter by letter. */
const GEORGIAN: Record<string, string> = {
  ა: 'a',
  ბ: 'b',
  გ: 'g',
  დ: 'd',
  ე: 'e',
  ვ: 'v',
  ზ: 'z',
  თ: 't',
  ი: 'i',
  კ: 'k',
  ლ: 'l',
  მ: 'm',
  ნ: 'n',
  ო: 'o',
  პ: 'p',
  ჟ: 'zh',
  რ: 'r',
  ს: 's',
  ტ: 't',
  უ: 'u',
  ფ: 'p',
  ქ: 'k',
  ღ: 'gh',
  ყ: 'q',
  შ: 'sh',
  ჩ: 'ch',
  ც: 'ts',
  ძ: 'dz',
  წ: 'ts',
  ჭ: 'ch',
  ხ: 'kh',
  ჯ: 'j',
  ჰ: 'h',
};

export function transliterateGeorgian(text: string): string {
  return Array.from(text, (ch) => GEORGIAN[ch] ?? ch).join('');
}

export function slugifyText(text: string): string {
  return transliterateGeorgian(text)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/, '');
}

/** The address a page's title asks for: its English title, else its Georgian one romanised, else "page". */
export function slugFromTitle(title: Record<string, string>): string {
  const candidates = [title['en'], title['ka'], ...Object.values(title)];
  for (const text of candidates) {
    const slug = text ? slugifyText(text) : '';
    if (slug.length >= 2) return isReservedSlug(slug) ? `${slug}-page` : slug;
  }
  return 'page';
}

function withSuffix(base: string, n: number): string {
  const suffix = `-${n}`;
  return `${base.slice(0, 63 - suffix.length).replace(/-+$/, '')}${suffix}`;
}

export interface SlugPage {
  id: string;
  title: Record<string, string>;
}

/**
 * Gives every page its address. A page in `fixed` keeps the address it has there: the one it was published
 * at, so a link a visitor saved never breaks, or one a platform admin set by hand. Every other page takes its
 * address from its title, numbered when that address is taken.
 */
export function assignPageSlugs<P extends SlugPage>(pages: P[], fixed: ReadonlyMap<string, string>): Array<P & { slug: string }> {
  const taken = new Set(pages.flatMap((page) => fixed.get(page.id) ?? []));
  return pages.map((page) => {
    const slug = fixed.get(page.id);
    if (slug) return { ...page, slug };
    const base = slugFromTitle(page.title);
    let next = base;
    for (let n = 2; taken.has(next); n++) next = withSuffix(base, n);
    taken.add(next);
    return { ...page, slug: next };
  });
}
